import {
    createContext,
    useCallback,
    useContext,
    useMemo,
    useState,
    type FC,
    type PropsWithChildren,
} from "react";

export interface SelectedCompany {
    id: number;
    name: string;
}

interface SelectedCompanyContextValue {
    selectedCompany: SelectedCompany | null;
    setSelectedCompany: (company: SelectedCompany | null) => void;
}

const STORAGE_KEY = "superAdminSelectedCompany";

const SelectedCompanyContext = createContext<SelectedCompanyContextValue>({
    selectedCompany: null,
    setSelectedCompany: () => {},
});

const readStored = (): SelectedCompany | null => {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (typeof parsed?.id === "number" && typeof parsed?.name === "string") {
            return parsed;
        }
        return null;
    } catch {
        return null;
    }
};

export const SelectedCompanyProvider: FC<PropsWithChildren> = ({ children }) => {
    const [selectedCompany, setSelectedCompanyState] =
        useState<SelectedCompany | null>(readStored);

    const setSelectedCompany = useCallback((company: SelectedCompany | null) => {
        setSelectedCompanyState(company);
        try {
            if (company) {
                localStorage.setItem(STORAGE_KEY, JSON.stringify(company));
            } else {
                localStorage.removeItem(STORAGE_KEY);
            }
        } catch {
            /* ignore storage failures */
        }
    }, []);

    const value = useMemo(
        () => ({ selectedCompany, setSelectedCompany }),
        [selectedCompany, setSelectedCompany]
    );

    return (
        <SelectedCompanyContext.Provider value={value}>
            {children}
        </SelectedCompanyContext.Provider>
    );
};

export const useSelectedCompany = () => useContext(SelectedCompanyContext);
